import { PROMPT_MODE } from '../constants/flowSection'
import {
  buildBusinessFlowFromLegacyFlatLlmsettings,
  buildLittleAssistantSnapshot,
  snapshotPromptMode,
} from './flowSectionHelpers'
import {
  applyExternalApisGroupFromSnapshot,
  buildExternalApisGroupSnapshot,
} from './externalApiHelpers'
import { applyEventTriggersFromSnapshot, buildEventTriggersSnapshot } from './eventTriggerHelpers'
import { createDefaultCallControl } from './callControlHelpers'
import {
  LLMSETTINGS_SCHEMA_VERSION,
  LLMSETTINGS_TAB_KEYS,
  LLMSETTINGS_LEGACY_CALL_CONTROL_TAB_KEY,
  buildTabbedLlmsettingsDocument,
  normalizeLlmsettingsTabKeys,
  validateLlmsettingsSchema,
} from './llmsettingsSchema'
import { applyIntentTagFromSnapshot, buildIntentTagSnapshot, createDefaultIntentTagState } from './intentTagHelpers'

function normalizeIntentTags(raw) {
  const list = Array.isArray(raw) ? raw : []
  return list.map((item) => {
    if (!item || typeof item !== 'object') {
      return { tag_name: '', tag_description: '', judge: 'prompt', conditions: [] }
    }
    const tag_name =
      item.tag_name != null
        ? String(item.tag_name)
        : item.name != null
          ? String(item.name)
          : ''
    const judgeRaw = item.judge != null ? String(item.judge) : item.judge_type != null ? String(item.judge_type) : ''
    const hasConditions = Array.isArray(item.conditions) && item.conditions.length > 0
    const judge = judgeRaw === 'rule' || (judgeRaw !== 'prompt' && hasConditions) ? 'rule' : 'prompt'
    const tag_description =
      item.tag_description != null
        ? String(item.tag_description)
        : item.rule != null
          ? String(item.rule)
          : ''
    const conditions = Array.isArray(item.conditions)
      ? item.conditions.map((cond) => ({
          field_id: cond && cond.field_id != null ? String(cond.field_id) : cond && cond.fieldId != null ? String(cond.fieldId) : '',
          operator: cond && cond.operator != null ? String(cond.operator) : 'eq',
          value: cond && cond.value != null ? String(cond.value) : '',
        }))
      : []
    return {
      tag_name,
      tag_description,
      judge,
      conditions,
      ranking:
        item.ranking != null && Number(item.ranking) > 0
          ? Math.floor(Number(item.ranking))
          : item.rank != null && Number(item.rank) > 0
            ? Math.floor(Number(item.rank))
            : undefined,
    }
  })
}

function migrateAssistantSettingsBlock(src) {
  if (src && typeof src === 'object' && Object.keys(src).length) {
    const la = src
    let intentLexiconId = ''
    if (la.intent_ref && la.intent_ref.id != null) {
      intentLexiconId = String(la.intent_ref.id)
    } else if (la.intent_lexicon_id != null) {
      intentLexiconId = String(la.intent_lexicon_id)
    }
    const sentences = Array.isArray(la.sentences)
      ? la.sentences
      : Array.isArray(la.keywords)
        ? la.keywords
        : []
    return buildLittleAssistantSnapshot({
      name: la.name,
      enabled: la.enabled,
      intentLexiconId,
      sentences,
    })
  }
  return buildLittleAssistantSnapshot(undefined)
}

function migrateCustomerInfoParamsBlock(src) {
  const raw = src
  if (raw && Array.isArray(raw.list)) {
    return raw
  }
  if (Array.isArray(raw)) {
    return {
      list: raw.map((r) => ({
        name: String(r.name != null ? r.name : '').trim(),
        key: String(r.key != null ? r.key : '').trim(),
      })),
      count: `${raw.length}/15`,
    }
  }
  return { list: [], count: '0/15' }
}

function migrateParameterConfig(pc) {
  if (!pc || typeof pc !== 'object') return {}
  if (pc.omni || pc.tts || pc.asr) {
    const next = { ...pc }
    delete next.list
    delete next.count
    delete next.engine
    return next
  }
  if (Array.isArray(pc.list)) {
    const n = pc.list.length
    return {
      // 旧扁平 list 仅有一份，归入至尊版 omni；旗舰版 TTS/ASR 保持空
      omni: {
        list: pc.list,
        count: pc.count != null ? String(pc.count) : `${n}/15`,
      },
      tts: {
        list: [],
        count: '0/15',
      },
      asr: {
        list: [],
        count: '0/15',
      },
    }
  }
  return {}
}

function normalizeCallControlBlock(cc) {
  const src = cc && typeof cc === 'object' ? { ...cc } : {}
  const defaults = createDefaultCallControl()

  if (src.input_timeout == null && src.idle_time != null && Number.isFinite(Number(src.idle_time))) {
    src.input_timeout = Math.max(1, Math.floor(Number(src.idle_time) * 1000))
  }
  if (src.max_timeouts == null && src.repeat_count != null && Number.isFinite(Number(src.repeat_count))) {
    src.max_timeouts = Math.max(1, Math.floor(Number(src.repeat_count)))
  }
  if (
    (!Array.isArray(src.silence_prompts) || !src.silence_prompts.length) &&
    src.warning_message != null &&
    String(src.warning_message).trim()
  ) {
    src.silence_prompts = [{ text: String(src.warning_message), audio_file_path: '' }]
  }
  if (src.goodbye == null && src.goodbye_message != null) {
    src.goodbye = { text: String(src.goodbye_message), audio_file_path: '' }
  }
  if (src.is_hmc == null && src.isHmc != null) {
    src.is_hmc = !!src.isHmc
  }

  delete src.idle_time
  delete src.repeat_count
  delete src.warning_message
  delete src.goodbye_message
  delete src.isHmc
  delete src.presetOpeningEnabled
  delete src.transitionSoundEnabled

  if (src.max_call_duration == null) src.max_call_duration = defaults.max_call_duration
  if (src.call_timeout == null) src.call_timeout = defaults.call_timeout
  if (src.input_timeout == null) src.input_timeout = defaults.input_timeout
  if (src.max_timeouts == null) src.max_timeouts = defaults.max_timeouts

  return src
}

function flattenPartialV2(src) {
  const raw = src && typeof src === 'object' ? src : {}
  if (Number(raw.schema_version) === LLMSETTINGS_SCHEMA_VERSION && raw[LLMSETTINGS_TAB_KEYS.PROMPT_CONFIG]) {
    const pc = raw[LLMSETTINGS_TAB_KEYS.PROMPT_CONFIG] || {}
    const vs = raw[LLMSETTINGS_TAB_KEYS.VOICE_SETTINGS] || {}
    const it = raw[LLMSETTINGS_TAB_KEYS.INTENT_AND_TAGS] || {}
    const ccTab =
      raw[LLMSETTINGS_TAB_KEYS.CALL_CONTROL] ||
      raw[LLMSETTINGS_LEGACY_CALL_CONTROL_TAB_KEY] ||
      {}
    return {
      role_setting: pc.role_setting,
      business_flow: pc.business_flow,
      custom_prompt: pc.custom_prompt,
      prompt_mode: pc.prompt_mode,
      voice_settings: { ...vs, call_control: ccTab.call_control || vs.call_control },
      intent_tags: it.intent_tags,
      intent_mode: it.intent_mode,
      extract_form: it.extract_form,
      extract_form_name: it.extract_form_name,
      extract_fields: it.extract_fields,
      rating_rules: it.rating_rules,
      custom_tools: ccTab.custom_tools,
      external_apis: ccTab.external_apis,
      external_apis_json: ccTab.external_apis_json,
      external_apis_form: ccTab.external_apis_form,
      event_triggers: ccTab.event_triggers,
      assistant_settings: ccTab.assistant_settings,
      customer_info_params: ccTab.customer_info_params,
    }
  }
  return { ...raw }
}

/**
 * 将旧版 scene.llmsettings 迁移为 schema_version 2（仅内存灌表单，不写回 scene；不读写 settings.llm）
 */
export function migrateLlmsettingsToV2(raw) {
  const flat = flattenPartialV2(raw)

  const role_setting =
    flat.role_setting && typeof flat.role_setting === 'object' ? flat.role_setting : {}

  let business_flow = Array.isArray(flat.business_flow) ? flat.business_flow : []
  if (!business_flow.length) {
    business_flow = buildBusinessFlowFromLegacyFlatLlmsettings(flat)
  }

  let custom_prompt = flat.custom_prompt != null ? String(flat.custom_prompt) : ''
  if (!String(custom_prompt).trim() && flat.free_prompt != null) {
    custom_prompt = String(flat.free_prompt)
  }

  const prompt_mode = snapshotPromptMode(flat.prompt_mode || PROMPT_MODE.FREE)
  const intentUi = applyIntentTagFromSnapshot(
    {
      intent_mode: flat.intent_mode,
      intent_tags: normalizeIntentTags(flat.intent_tags),
      extract_form: flat.extract_form,
      extract_form_name: flat.extract_form_name,
      extract_fields: flat.extract_fields,
      rating_rules: flat.rating_rules,
    },
    createDefaultIntentTagState(),
  )
  const intentSnapshot = buildIntentTagSnapshot(intentUi)

  let voice_settings =
    flat.voice_settings && typeof flat.voice_settings === 'object' ? { ...flat.voice_settings } : {}
  let call_control =
    voice_settings.call_control && typeof voice_settings.call_control === 'object'
      ? normalizeCallControlBlock(voice_settings.call_control)
      : normalizeCallControlBlock(createDefaultCallControl())
  delete voice_settings.call_control
  voice_settings.parameter_config = migrateParameterConfig(voice_settings.parameter_config)

  const assistant_settings = migrateAssistantSettingsBlock(
    flat.assistant_settings || flat.little_assistant,
  )
  const customer_info_params = migrateCustomerInfoParamsBlock(flat.customer_info_params)

  const custom_tools = flat.custom_tools != null ? String(flat.custom_tools) : ''
  const externalApisGroup = applyExternalApisGroupFromSnapshot(
    {
      external_apis: flat.external_apis,
      external_apis_json: flat.external_apis_json,
      external_apis_form: flat.external_apis_form,
    },
    custom_tools,
  )
  const externalApisSnapshot = buildExternalApisGroupSnapshot(
    externalApisGroup.json,
    externalApisGroup.form,
  )
  const eventTriggers = applyEventTriggersFromSnapshot(flat.event_triggers)

  const voiceSnapshot = {
    ...voice_settings,
    call_control,
  }

  return buildTabbedLlmsettingsDocument({
    role_setting,
    business_flow,
    custom_prompt,
    prompt_mode,
    voice_settings: voiceSnapshot,
    intent_mode: intentSnapshot.intent_mode,
    intent_tags: intentSnapshot.intent_tags,
    extract_form: intentSnapshot.extract_form,
    rating_rules: intentSnapshot.rating_rules,
    custom_tools,
    external_apis_json: externalApisSnapshot.external_apis_json,
    external_apis_form: externalApisSnapshot.external_apis_form,
    event_triggers: buildEventTriggersSnapshot(eventTriggers),
    assistant_settings,
    customer_info_params,
  })
}

/**
 * 刷新/回滚时：已是 v2 则原样返回；否则仅基于 llmsettings 迁移为 v2 再灌表单。
 * 保存发布时只写 scene.llmsettings，不改动 scene.settings.llm。
 */
export function coerceLlmsettingsForUi(raw) {
  const src = normalizeLlmsettingsTabKeys(raw && typeof raw === 'object' ? raw : {})
  const check = validateLlmsettingsSchema(src)
  if (check.valid) {
    return src
  }
  return migrateLlmsettingsToV2(src)
}
